import { createTheme, responsiveFontSizes } from '@mui/material/styles';

/** Main wordmark (light backgrounds); keep under `public/` only so large files are not webpack-bundled. */
export const siteLogoUrl = '/logo.svg';
/** White wordmark for navy / dark section backgrounds. */
export const siteLogoUrlOnDark = '/logo-white.png';

export const brand = {
  navy: '#0F172A',
  navyDeep: '#0A0F1F',
  blue: '#1E5BFF',
  blueLight: '#7CC0FF',
  gold: '#C9A14A',
  goldSoft: '#E5C97A',
  cream: '#F8FAFC',
  slate: '#475569',
  border: '#E2E8F0',
  /** Navy panel + corner glows (CTA strip, dark feature / step cards). */
  ctaPanelGradient:
    'radial-gradient(700px 320px at 110% 0%, rgba(30,91,255,0.55), transparent 60%),' +
    'radial-gradient(500px 240px at -10% 100%, rgba(201,161,74,0.30), transparent 65%)',
  /** Icons on dark navy panels (Concierge product cards). */
  amberAccent: '#f59e0b',
};

const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brand.blue,
      dark: brand.navy,
      contrastText: '#ffffff',
    },
    secondary: {
      main: brand.gold,
      light: brand.goldSoft,
      contrastText: brand.navy,
    },
    info: {
      main: brand.blueLight,
    },
    background: {
      default: brand.cream,
      paper: '#FFFFFF',
    },
    text: {
      primary: brand.navy,
      secondary: brand.slate,
    },
    divider: brand.border,
  },
  shape: {
    borderRadius: 14,
  },
  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    h1: {
      fontFamily: "'Manrope', 'Inter', sans-serif",
      fontWeight: 800,
      letterSpacing: '-0.03em',
      lineHeight: 1.05,
    },
    h2: {
      fontFamily: "'Manrope', 'Inter', sans-serif",
      fontWeight: 800,
      letterSpacing: '-0.025em',
      lineHeight: 1.1,
    },
    h3: {
      fontFamily: "'Manrope', 'Inter', sans-serif",
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.15,
    },
    h4: {
      fontFamily: "'Manrope', 'Inter', sans-serif",
      fontWeight: 700,
      letterSpacing: '-0.015em',
    },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', fontSize: '0.8rem' },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
    body1: { fontSize: '1.05rem', lineHeight: 1.65 },
    body2: { fontSize: '0.95rem', lineHeight: 1.6 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          paddingInline: 22,
          paddingBlock: 10,
        },
        containedPrimary: {
          boxShadow: '0 8px 24px -8px rgba(30,91,255,0.55)',
          '&:hover': {
            boxShadow: '0 12px 28px -8px rgba(30,91,255,0.7)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': { borderWidth: 1.5 },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backdropFilter: 'saturate(140%) blur(10px)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brand.cream,
        },
      },
    },
  },
});

const theme = responsiveFontSizes(baseTheme);

export default theme;
